import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/ui-bits";
import { fmtINR, fmtDate, fmtNum } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Gavel, Scales, Warning, CalendarCheck, MagnifyingGlass, FilePdf, PencilSimple, ArrowSquareOut, FileCsv } from "@phosphor-icons/react";

const STATUS_COLOR = {
  "Legal Initiated": "bg-blue-100 text-blue-800",
  "Notice Sent": "bg-violet-100 text-violet-800",
  "Sec 138 - Notice Pending": "bg-red-100 text-red-800",
  "Case Filed": "bg-amber-100 text-amber-800",
  "Hearing Scheduled": "bg-orange-100 text-orange-800",
  "Awarded": "bg-emerald-100 text-emerald-800",
  "Closed": "bg-slate-200 text-slate-700",
};

export default function Legal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [opts, setOpts] = useState({ statuses: [] });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const canManage = ["super_admin", "client_admin", "manager"].includes(user.role);

  useEffect(() => { api.get("/legal/options").then((r) => setOpts(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (status) params.legal_status = status;
    const [s, a] = await Promise.all([
      api.get("/legal/stats").catch(() => ({ data: null })),
      api.get("/legal/accounts", { params }).catch(() => ({ data: [] })),
    ]);
    setStats(s.data); setRows(a.data); setLoading(false);
  }, [q, status]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="legal-page">
      <PageHeader title="Legal Cases" subtitle="Track legal-candidate accounts, Sec 138 notices, hearings & status">
        <a href={`${API}/reports/legal/export?format=csv`}>
          <Button variant="outline" className="rounded-sm gap-2" data-testid="legal-export-csv"><FileCsv size={16} weight="bold" /> Export</Button>
        </a>
      </PageHeader>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
          <StatCard label="Legal Cases" value={fmtNum(stats.total)} icon={Scales} color="text-primary" testId="legal-stat-total" />
          <StatCard label="Legal Outstanding" value={fmtINR(stats.total_outstanding || 0)} icon={Scales} color="text-rose-600" testId="legal-stat-outstanding" />
          <StatCard label="Sec 138 (Cheque)" value={fmtNum(stats.sec138)} icon={Warning} color="text-red-600" testId="legal-stat-sec138" />
          <StatCard label="Hearings This Week" value={fmtNum(stats.hearings_this_week)} icon={CalendarCheck} color="text-orange-600" testId="legal-stat-hearings" />
          <StatCard label="Awarded" value={fmtNum(stats.awarded)} icon={Gavel} color="text-emerald-600" testId="legal-stat-awarded" />
          <StatCard label="Closed" value={fmtNum(stats.closed)} icon={Scales} color="text-muted-foreground" testId="legal-stat-closed" />
        </div>
      )}

      {/* Outstanding by legal stage */}
      {stats?.outstanding_by_status?.length > 0 && (
        <div className="border border-border bg-card p-4 mb-5" data-testid="legal-outstanding-breakdown">
          <div className="flex items-center justify-between mb-3">
            <span className="label-caps">Outstanding by Legal Stage</span>
            <span className="text-xs text-muted-foreground mono">Total {fmtINR(stats.total_outstanding || 0)}</span>
          </div>
          <div className="space-y-2.5">
            {(() => {
              const maxOut = Math.max(...stats.outstanding_by_status.map((s) => s.outstanding), 1);
              return stats.outstanding_by_status.map((s) => {
                const pct = stats.total_outstanding ? Math.round((s.outstanding / stats.total_outstanding) * 100) : 0;
                const bar = Math.round((s.outstanding / maxOut) * 100);
                return (
                  <button
                    key={s.status}
                    onClick={() => setStatus(status === s.status ? "" : s.status)}
                    className="w-full text-left group"
                    data-testid={`legal-stage-${s.status}`}
                  >
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 font-semibold rounded-sm ${STATUS_COLOR[s.status] || "bg-secondary"}`}>{s.status}</span>
                        <span className="text-muted-foreground">{s.count} {s.count === 1 ? "case" : "cases"}</span>
                      </span>
                      <span className="mono font-semibold">{fmtINR(s.outstanding)} <span className="text-muted-foreground font-normal">({pct}%)</span></span>
                    </div>
                    <div className="h-2 w-full bg-secondary rounded-sm overflow-hidden">
                      <div className="h-full bg-rose-500/80 group-hover:bg-rose-600 transition-colors" style={{ width: `${bar}%` }} />
                    </div>
                  </button>
                );
              });
            })()}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search name, loan ID or case no…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-sm" data-testid="legal-search" />
        </div>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-56 rounded-sm" data-testid="legal-status-filter"><SelectValue placeholder="Legal Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Legal Statuses</SelectItem>
            {opts.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="label-caps border-b border-border bg-secondary/40">
              <th className="text-left p-3">Loan / Borrower</th>
              <th className="text-right p-3">Outstanding</th>
              <th className="text-left p-3">Legal Status</th>
              <th className="text-left p-3">Court Case No</th>
              <th className="text-left p-3">Advocate</th>
              <th className="text-left p-3">Next Hearing</th>
              <th className="text-center p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => {
              const soon = a.next_hearing_date && a.next_hearing_date >= today;
              return (
                <tr key={a.account_id} className="border-b border-border last:border-0 hover:bg-secondary/50" data-testid={`legal-row-${a.loan_id}`}>
                  <td className="p-3">
                    <div className="font-medium flex items-center gap-2">{a.borrower_name}
                      {a.cheque_bounced && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-red-100 text-red-700">SEC 138</span>}
                    </div>
                    <div className="text-xs text-muted-foreground mono">{a.loan_id} • {a.assigned_agent_name || "Unassigned"}</div>
                  </td>
                  <td className="p-3 text-right mono font-semibold">{fmtINR(a.outstanding_amount)}</td>
                  <td className="p-3"><span className={`px-2 py-0.5 text-[11px] font-semibold rounded-sm ${STATUS_COLOR[a.legal_status] || "bg-secondary"}`}>{a.legal_status || "—"}</span></td>
                  <td className="p-3 mono text-xs">{a.court_case_no || "—"}</td>
                  <td className="p-3 text-xs">{a.advocate || "—"}</td>
                  <td className="p-3 text-xs mono"><span className={soon ? "text-orange-600 font-semibold" : "text-muted-foreground"}>{a.next_hearing_date ? fmtDate(a.next_hearing_date) : "—"}</span></td>
                  <td className="p-3">
                    <div className="flex items-center justify-center gap-1">
                      {canManage && (
                        <button onClick={() => setEdit(a)} title="Manage legal" className="h-8 w-8 inline-flex items-center justify-center rounded-sm text-primary hover:bg-primary/10" data-testid={`legal-manage-${a.loan_id}`}>
                          <PencilSimple size={15} weight="bold" />
                        </button>
                      )}
                      <a href={`${API}/accounts/${a.account_id}/legal-notice?type=${a.cheque_bounced ? "sec138" : "demand"}`} target="_blank" rel="noreferrer" title="Legal notice PDF">
                        <button className="h-8 w-8 inline-flex items-center justify-center rounded-sm text-rose-600 hover:bg-rose-50" data-testid={`legal-notice-${a.loan_id}`}><FilePdf size={15} weight="bold" /></button>
                      </a>
                      <button onClick={() => navigate(`/accounts/${a.account_id}`)} title="Open account" className="h-8 w-8 inline-flex items-center justify-center rounded-sm text-muted-foreground hover:bg-secondary" data-testid={`legal-open-${a.loan_id}`}><ArrowSquareOut size={15} weight="bold" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && rows.length === 0 && <Empty text="No legal cases. Use 'Process Legal' or 'Cheque Bounced' in Log Activity to add one." />}
      </div>

      {edit && <LegalDialog account={edit} statuses={opts.statuses} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, testId }) {
  return (
    <div className="border border-border bg-card p-4" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="label-caps">{label}</span>
        <Icon size={16} weight="bold" className={color} />
      </div>
      <div className={`text-2xl font-black font-heading mono mt-2 ${color}`}>{value}</div>
    </div>
  );
}

function LegalDialog({ account, statuses, onClose, onSaved }) {
  const [f, setF] = useState({
    legal_status: account.legal_status || "Legal Initiated",
    court_case_no: account.court_case_no || "",
    next_hearing_date: account.next_hearing_date || "",
    advocate: account.advocate || "",
    note: "",
  });
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { api.get(`/accounts/${account.account_id}/legal-history`).then((r) => setHistory(r.data)).catch(() => {}); }, [account.account_id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/accounts/${account.account_id}/legal`, f);
      toast.success("Legal status updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-h-[90vh] overflow-y-auto" data-testid="legal-dialog">
        <DialogHeader>
          <DialogTitle>Manage Legal — {account.borrower_name}</DialogTitle>
          <DialogDescription>{account.loan_id} • Update legal status, case details and hearing. A reminder is created for the hearing date.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Legal Status</Label>
            <Select value={f.legal_status} onValueChange={(v) => upd("legal_status", v)}>
              <SelectTrigger className="rounded-sm" data-testid="legal-dialog-status"><SelectValue /></SelectTrigger>
              <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Court Case No.</Label><Input className="rounded-sm" value={f.court_case_no} onChange={(e) => upd("court_case_no", e.target.value)} data-testid="legal-dialog-case" /></div>
            <div><Label className="text-xs">Next Hearing Date</Label><Input type="date" className="rounded-sm" value={f.next_hearing_date} onChange={(e) => upd("next_hearing_date", e.target.value)} data-testid="legal-dialog-hearing" /></div>
          </div>
          <div><Label className="text-xs">Advocate</Label><Input className="rounded-sm" value={f.advocate} onChange={(e) => upd("advocate", e.target.value)} data-testid="legal-dialog-advocate" /></div>
          <div><Label className="text-xs">Note</Label><Textarea className="rounded-sm" value={f.note} onChange={(e) => upd("note", e.target.value)} data-testid="legal-dialog-note" /></div>

          {history.length > 0 && (
            <div>
              <Label className="text-xs">History</Label>
              <div className="border border-border rounded-sm divide-y divide-border max-h-40 overflow-y-auto mt-1">
                {history.map((h) => (
                  <div key={h.id} className="p-2.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{h.from_status ? `${h.from_status} → ` : ""}{h.to_status}</span>
                      <span className="text-muted-foreground mono">{fmtDate(h.created_at)}</span>
                    </div>
                    {h.note && <div className="text-muted-foreground mt-0.5">{h.note}</div>}
                    <div className="text-muted-foreground mt-0.5">by {h.actor}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter><Button onClick={save} disabled={saving} className="rounded-sm" data-testid="legal-dialog-save">{saving ? "Saving…" : "Update Legal Status"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
